/**
 * GUC v2 — Subject property.
 *
 * SINGLE PROPERTY, per spec §2 ("single-property UI is fine for v2; the model
 * accepts more"). The wire shape is still the `subjectProperties[]` array the
 * loan model uses, so nothing about the payload narrows — only the form does,
 * and a ground-up scenario being sized is a project, not a portfolio.
 *
 * The fields that make this a GUC form and not the RTL one:
 *   • completed work split into HARD and SOFT costs — the backend sums them into
 *     `rehabExpendedAtFunding`, which is what feeds the securitization cost
 *     basis. Entering the total directly is the thing the split replaces.
 *   • `approvedPermitsAndPlans` as three states — only an explicit No fires the
 *     −10% FRONT-END reduction, and the label says so.
 *   • interest reserve as MONTHS (blank = the required months), with the
 *     dollars derived server-side, plus the required "funded by" answer — and
 *     the same financed / borrower-cash answer for closing costs (shared with
 *     the RTL sizer, sizersShared/FinancedCostsFields).
 */
import { useField } from "react-final-form";

import {
  SZ,
  Section,
  Grid,
  Field2,
  RffBoolSelect,
  RffDate,
  RffMoney,
  RffNum,
  RffSelect,
  RffStepper,
  RffSwitch,
  SubLabel,
  SizerMuiField,
} from "../../sizersShared/sizerUi";
import RffAddressEditingFieldGoogle from "../../../components/finalForm/inputs/RffAddressEditingFieldGoogle";
import RffSelectField from "../../../components/finalForm/inputs/RffSelectField";
import {
  selectOptionsPropertyType,
  selectOptionsPropertyStatusAtOrigination,
} from "../../sizersShared/sizerSelectOptions";
import { gucOptionsApprovedPermits } from "../gucConstants";
import { isPurchasePurpose } from "../gucLoanMath";
import {
  ClosingCostsFields,
  InterestReserveFields,
} from "../../sizersShared/FinancedCostsFields";

/** The address row's two controls are different beasts; one height holds them level. */
const FIELD_H = 42;

const TYPE_SELECT_SX = {
  "& .MuiOutlinedInput-root": { height: FIELD_H, minHeight: FIELD_H },
};

const ADDRESS_ROW_SX = {
  "& .input-wrapper-root .MuiInputLabel-root": {
    height: 22,
    display: "flex",
    alignItems: "center",
    fontSize: 13,
    fontWeight: 500,
    color: SZ.ink500,
    marginBottom: "6px",
  },
  "& .input-wrapper-root > div": {
    borderColor: SZ.lineStrong,
    borderRadius: SZ.radiusSm,
    minHeight: FIELD_H,
  },
  "& .input-wrapper-root > div > div > div > div": {
    paddingTop: "8px",
    paddingBottom: "8px",
  },
  "& .input-wrapper-root .MuiIconButton-root": { padding: "4px" },
  "& .input-wrapper-root .MuiIconButton-root .MuiSvgIcon-root": { fontSize: 17 },
};

/**
 * A blank property. `address` is left EMPTY, not pre-shaped — the Places picker
 * writes the whole Address object in one go, and seeding partial keys only makes
 * a half-filled address look real. No default property type either: it is a
 * required answer, and a silent "SFR" answers a question nobody was asked.
 */
export const newGucProperty = () => ({
  address: {},
  propertyType: "",
  numberOfUnits: 1,
  floodZone: false,
  propertyRuralIndicator: false,
});

export default function GucPropertySection({ first, result }) {
  const { input: loanPurpose } = useField("loanPurpose", {
    subscription: { value: true },
  });
  const { input: delayedFlag } = useField("delayedPurchase", {
    subscription: { value: true },
  });
  const isPurchase = isPurchasePurpose(loanPurpose.value, delayedFlag.value);
  const f = "subjectProperties[0]";

  return (
    <Section
      first={first}
      title="Subject property"
      subtitle="The project this loan is built around."
    >
      <Grid cols={12}>
        <Field2 span={8}>
          <SizerMuiField sx={ADDRESS_ROW_SX}>
            <RffAddressEditingFieldGoogle
              name={`${f}.address`}
              label="Property address"
              allowManualEntry
            />
          </SizerMuiField>
        </Field2>
        <Field2 label="Property type" required span={4}>
          <SizerMuiField sx={TYPE_SELECT_SX}>
            <RffSelectField
              name={`${f}.propertyType`}
              options={selectOptionsPropertyType}
              required
              sm
            />
          </SizerMuiField>
        </Field2>
      </Grid>

      <SubLabel>Values &amp; basis</SubLabel>
      <Grid cols={2}>
        <Field2
          label={isPurchase ? "Purchase price" : "Original purchase price"}
          required={isPurchase}
        >
          <RffMoney name={`${f}.purchasePrice`} placeholder="0" required={isPurchase} />
        </Field2>
        <Field2
          label={isPurchase ? "Purchase date" : "Original purchase date"}
          required={isPurchase}
        >
          <RffDate name={`${f}.purchaseDate`} required={isPurchase} />
        </Field2>
        <Field2 label="As-is value" tip="The refinance cost basis, and the LTV denominator.">
          <RffMoney name={`${f}.asIsValue`} placeholder="0" />
        </Field2>
        <Field2 label="As-completed value (ARV)">
          <RffMoney name={`${f}.arv`} placeholder="0" />
        </Field2>
        {!isPurchase && (
          <Field2
            label="Additional basis"
            tip="Refi / rehab carry — adds to the LTC cost basis."
          >
            <RffMoney name={`${f}.additionalBasis`} placeholder="0" />
          </Field2>
        )}
        <Field2
          label="Construction budget"
          tip="Hard and soft costs of the work still to be done. The financed share of this is the holdback."
        >
          <RffMoney name={`${f}.constructionBudget`} placeholder="0" />
        </Field2>
      </Grid>

      {/* COMPLETED WORK, SPLIT. The backend sums hard + soft into
          `rehabExpendedAtFunding` and reports a warning if a directly-entered
          total disagrees with the split — the split is the input. */}
      <SubLabel note="sums into rehab expended at funding">
        Completed work
      </SubLabel>
      <Grid cols={2}>
        <Field2
          label="Completed work — hard costs"
          tip="Verified mortgagor-paid construction already expended. Feeds the securitization cost basis on a purchase."
        >
          <RffMoney name={`${f}.completedWorkHardCosts`} placeholder="0" />
        </Field2>
        <Field2
          label="Completed work — soft costs"
          tip="Architecture, engineering, permits and fees already paid."
        >
          <RffMoney name={`${f}.completedWorkSoftCosts`} placeholder="0" />
        </Field2>
      </Grid>

      <SubLabel>Entitlement &amp; status</SubLabel>
      <Grid cols={2}>
        {/* THREE STATES. Only an explicit No fires the reduction; unanswered
            takes none, and saying so on the option label is the whole reason
            this is not a toggle. */}
        <Field2
          label="Approved permits and plans"
          hint="Only an explicit No fires the −10% front-end reduction (LTV/LTC only). Left unanswered, nothing is reduced."
        >
          <RffBoolSelect
            name={`${f}.approvedPermitsAndPlans`}
            options={gucOptionsApprovedPermits}
            placeholder="Not stated"
          />
        </Field2>
        <Field2 label="Status at origination">
          <RffSelect
            name={`${f}.propertyStatusAtOrigination`}
            options={selectOptionsPropertyStatusAtOrigination}
            placeholder="Select status"
          />
        </Field2>
      </Grid>

      {/* Closing costs and the interest reserve, each financed or borrower
          cash (pricing-scenario-sizers.md §12b). Ground up always owes the
          reserve answer. */}
      <ClosingCostsFields name={f} index={0} result={result} isPurchase={isPurchase} />
      <InterestReserveFields name={f} index={0} result={result} askFunding />

      <SubLabel>Property</SubLabel>
      <Grid cols={4}>
        <Field2 label="Units">
          <RffStepper name={`${f}.numberOfUnits`} placeholder="1" min={1} />
        </Field2>
        <Field2 label="Sq. footage">
          <RffNum name={`${f}.residentialSqFootage`} placeholder="0" />
        </Field2>
        <Field2 label="Flood zone">
          <RffSwitch name={`${f}.floodZone`} />
        </Field2>
        <Field2 label="Rural">
          <RffSwitch name={`${f}.propertyRuralIndicator`} />
        </Field2>
      </Grid>

      {/* Both feed the property model's own feasibility test — a >20% expansion
          or a change of use is one of its four triggers for a third-party
          feasibility report, which comes back as an INFO finding, not a block. */}
      <Grid cols={2} sx={{ mt: "24px" }}>
        <Field2
          label="Sq. footage after the project"
          tip="Leave blank if the project does not change the footprint."
        >
          <RffNum name={`${f}.afterRenoSqFootage`} placeholder="0" />
        </Field2>
        <Field2 label="Changes primary use or zoning">
          <RffSwitch name={`${f}.changeOfUseFlag`} />
        </Field2>
      </Grid>
    </Section>
  );
}
